/**
 * instant-prefetch.js
 * Warms the browser cache for internal page navigations so clicks feel instant.
 *
 * Uses <link rel="prefetch"> ONLY. This fetches the destination HTML at LOW,
 * cancellable priority into the HTTP cache. It does NOT render the page or run
 * its scripts, so no analytics/pixels fire on prefetch and no server state
 * changes beyond a normal GET. When the visitor actually clicks, the browser
 * serves the already-cached response.
 *
 * Safe by construction (no cons):
 *   - same-origin, http(s), GET-style links only
 *   - skips cart / checkout / account / orders / logout / gift_cards and any
 *     query-string or in-page-anchor links (stateful or non-navigational)
 *   - honors data-no-instant opt-out (already present on cart remove links)
 *   - skips new-tab targets, downloads, mailto:/tel:
 *   - respects Save-Data and slow (2g) connections
 *   - prefetches each URL at most once, capped to avoid runaway prefetching
 *   - fires on hover-intent (small delay) + mousedown + touchstart, so we only
 *     spend bandwidth on links the visitor signals intent to open
 */
(function () {
  'use strict';

  // Bail if the browser can't prefetch.
  var probe = document.createElement('link');
  if (!(probe.relList && probe.relList.supports && probe.relList.supports('prefetch'))) {
    return;
  }

  // Respect data-saver and very slow connections.
  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) {
    return;
  }

  var done = Object.create(null);
  var doneCount = 0;
  var MAX = 60;
  var hoverTimer = null;

  // State-changing or personalized routes we must never prefetch.
  var BLOCKED = /\/(cart|checkout|account|orders|gift_cards|logout)(\/|$)/;

  function shouldSkip(a, url) {
    if (a.hasAttribute('data-no-instant')) return true;
    if (a.hasAttribute('download')) return true;
    if (a.target && a.target !== '_self') return true;
    if (url.origin !== location.origin) return true;        // same-origin only
    if (url.protocol !== location.protocol) return true;    // excludes mailto:, tel:
    if (url.search) return true;                            // query string → often stateful/filtered
    if (url.pathname === location.pathname) return true;    // same page / in-page anchor
    if (BLOCKED.test(url.pathname)) return true;
    return false;
  }

  function hrefFor(target) {
    var a = target && target.closest ? target.closest('a[href]') : null;
    if (!a) return null;
    var url;
    try {
      url = new URL(a.href);
    } catch (e) {
      return null;
    }
    return shouldSkip(a, url) ? null : url.href;
  }

  function prefetch(href) {
    if (done[href] || doneCount >= MAX) return;
    done[href] = true;
    doneCount++;
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    document.head.appendChild(link);
  }

  // Desktop: hover with a short delay (ignore quick fly-overs) + immediate on mousedown.
  document.addEventListener('mouseover', function (e) {
    var href = hrefFor(e.target);
    if (!href) return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(function () { prefetch(href); }, 65);
  }, { capture: true, passive: true });

  document.addEventListener('mouseout', function () {
    if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; }
  }, { capture: true, passive: true });

  document.addEventListener('mousedown', function (e) {
    var href = hrefFor(e.target);
    if (href) prefetch(href);
  }, { capture: true, passive: true });

  // Mobile: prefetch on touchstart (fires ~100-200ms before the click completes).
  document.addEventListener('touchstart', function (e) {
    var href = hrefFor(e.target);
    if (href) prefetch(href);
  }, { capture: true, passive: true });
})();
